import { create } from 'zustand';
import type {
  Session,
  GetMySessionsParams,
  SessionTerminationResponse,
} from 'src/types/session';
import { getCurrentTenantId } from './tenant';
import {
  deleteMySessionById,
  deleteMySessions,
  getMySessions,
} from 'src/services/sessions';
import {
  captureSessionException,
  captureSessionMessage,
} from 'src/utils/sentry/sessionSentry';

interface SessionState {
  // State
  sessions: Session[]; // List of user sessions
  currentSession: Session | null; // Current active session
  isLoading: boolean; // Loading indicator
  error: string | null; // Error message
  lastFetch: number | null; // Timestamp of last fetch

  // Actions
  actions: {
    setSessions: (sessions: Session[]) => void;
    setCurrentSession: (session: Session | null) => void;
    setLoading: (loading: boolean) => void;
    setError: (error: string | null) => void;
    fetchSessions: (params?: GetMySessionsParams) => Promise<void>;
    terminateSession: (
      sessionId: string,
      reason?: string,
    ) => Promise<SessionTerminationResponse>;
    terminateAllSessions: (
      tenantId?: string,
      reason?: string,
    ) => Promise<SessionTerminationResponse>;
    clearSessions: () => void;
  };
}

export const useSessionStore = create<SessionState>((set, get) => ({
  sessions: [],
  currentSession: null,
  isLoading: false,
  error: null,
  lastFetch: null,

  actions: {
    setSessions: (sessions) => {
      const currentSession = sessions.find((s) => s.isCurrent) || null;
      set({ sessions, currentSession });
    },

    setCurrentSession: (session) => {
      set({ currentSession: session });
    },

    setLoading: (loading) => {
      set({ isLoading: loading });
    },

    setError: (error) => {
      set({ error });
    },

    fetchSessions: async (params) => {
      set({ isLoading: true, error: null });

      try {
        captureSessionMessage('Fetching my sessions', 'info', {
          event: 'session.fetch.requested',
          tenantId: params?.tenantId ?? getCurrentTenantId() ?? undefined,
        });

        const tenantId = params?.tenantId || getCurrentTenantId() || undefined;
        const response = await getMySessions({
          ...params,
          tenantId,
        });

        const currentSession =
          response.sessions.find((s) => s.isCurrent) || null;

        set({
          sessions: response.sessions,
          currentSession,
          isLoading: false,
          lastFetch: Date.now(),
          error: null,
        });

        captureSessionMessage('Fetched my sessions', 'info', {
          event: 'session.fetch.succeeded',
          tenantId,
          extra: {
            sessionsCount: response.sessions.length,
            currentSessionId: currentSession?.id ?? null,
          },
        });
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to fetch sessions';

        set({
          isLoading: false,
          error: errorMessage,
        });

        captureSessionException(error, {
          event: 'session.fetch.failed',
          tenantId: params?.tenantId ?? getCurrentTenantId() ?? undefined,
          extra: { errorMessage },
        });
        throw error;
      }
    },

    terminateSession: async (sessionId) => {
      const state = get();
      set({ isLoading: true, error: null });

      try {
        const response = await deleteMySessionById(sessionId);

        // Remove terminated session from state
        const updatedSessions = state.sessions.filter(
          (s) => s.id !== sessionId,
        );
        const currentSession = updatedSessions.find((s) => s.isCurrent) || null;

        set({
          sessions: updatedSessions,
          currentSession,
          isLoading: false,
          error: null,
        });

        captureSessionMessage('Terminated my session', 'info', {
          event: 'session.terminate.succeeded',
          sessionId,
          extra: {
            deletedSessionWasCurrent: state.currentSession?.id === sessionId,
          },
        });

        return response;
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Failed to terminate session';
        set({
          isLoading: false,
          error: errorMessage,
        });

        captureSessionException(error, {
          event: 'session.terminate.failed',
          sessionId,
          extra: { errorMessage },
        });
        throw error;
      }
    },

    terminateAllSessions: async (tenantId, reason) => {
      const state = get();
      set({ isLoading: true, error: null });

      try {
        const effectiveTenantId = tenantId || getCurrentTenantId() || undefined;
        captureSessionMessage('Terminating all my sessions', 'warning', {
          event: 'session.terminateAll.requested',
          tenantId: effectiveTenantId,
          reason: reason ?? 'user_bulk_termination',
        });

        const response = await deleteMySessions({
          tenantId: effectiveTenantId,
          reason: reason || 'user_bulk_termination',
        });

        // Keep only the current session if it exists
        const currentSession = state.currentSession;
        const updatedSessions = currentSession ? [currentSession] : [];

        set({
          sessions: updatedSessions,
          currentSession,
          isLoading: false,
          error: null,
        });

        captureSessionMessage('Terminated all my sessions', 'warning', {
          event: 'session.terminateAll.succeeded',
          tenantId: effectiveTenantId,
          reason: reason ?? 'user_bulk_termination',
          extra: { hadCurrentSession: !!state.currentSession },
        });

        return response;
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Failed to terminate all sessions';
        set({
          isLoading: false,
          error: errorMessage,
        });

        captureSessionException(error, {
          event: 'session.terminateAll.failed',
          tenantId: tenantId ?? getCurrentTenantId() ?? undefined,
          reason,
          extra: { errorMessage },
        });
        throw error;
      }
    },

    clearSessions: () => {
      set({
        sessions: [],
        currentSession: null,
        error: null,
        lastFetch: null,
      });

      captureSessionMessage('Cleared sessions state', 'info', {
        event: 'session.state.cleared',
      });
    },
  },
}));
